import React, { useState } from 'react';

import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import { Button, ColorPicker, Form, Input, notification, Spin, Tooltip } from 'antd';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/system/useMediaQuery';
import errorHandler, { errorMessage } from 'errors/errorHandler';
import { ITags } from 'interfaces/tags';
import attendanceListService from 'services/attendanceList';

import ArchiveMarker from './ArchiveMarker';

interface IProps {
  marker: ITags;
  onCompleteEdit: (tag: Partial<ITags>) => void;
  onComplete: (tag: Partial<ITags>) => void;

  onMarkerRemoved: (tag: Partial<ITags>) => void;
}

const MarkerEditDialogItem: React.FC<IProps> = ({ marker, onComplete, onCompleteEdit, onMarkerRemoved }) => {
  const [model, setModel] = useState<ITags>(marker);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showModalArchiveMarker, setShowModalArchiveMarker] = useState(false);
  const [showItem, setShowItem] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const editMarker = (isValid: boolean) => {
    if (!isValid) {
      notification.error({ message: 'Verifique os campos obrigatórios' });
      return;
    }

    setSubmitLoading(true);

    if (model.isNew) {
      const modelToPost = {
        color: model.color,
        title: model.title
      };

      attendanceListService.addTag({ tags: [modelToPost] }).subscribe(
        res => {
          const newModel = {
            color: model.color,
            title: model.title,
            id_temp: model.id,
            id: res.data[0].id
          };
          notification.success({ message: 'Marcador salvo' });
          setModel(newModel);
          setSubmitLoading(false);
          onComplete(newModel);
        },
        ({ data: err }) => {
          setSubmitLoading(false);
          errorHandler(err, errorMessage.generic('ao editar marcador'));
        }
      );
    } else {
      attendanceListService.editTag(model).subscribe(
        () => {
          notification.success({ message: 'Marcador salvo' });
          onCompleteEdit(model);
          setSubmitLoading(false);
        },
        ({ data: err }) => {
          setSubmitLoading(false);
          errorHandler(err, errorMessage.generic('ao editar marcador'));
        }
      );
    }
  };

  const handleChange = (value: any, name: string) => {
    setModel({
      ...model,
      [name]: value
    });
  };

  const removeMarkerOnComplete = (tag: ITags) => {
    setShowModalArchiveMarker(false);
    setShowItem(false);
    onMarkerRemoved(tag);
  };

  return (
    <Form onFinish={editMarker} layout='vertical'>
      {showItem && (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Form.Item
              label='Título'
              required
              rules={[
                { required: true, message: 'O campo nome é obrigatório' },
                { whitespace: true },
                { max: 225, message: 'O nome não pode conter mais que 225 caracteres' }
              ]}
            >
              <Input
                placeholder='Título do marcador'
                onChange={e => handleChange(e.target.value, 'title')}
                value={model.title || ''}
                id='markerdialog-markertitle-input'
              />
            </Form.Item>

            <Form.Item label='Cor' required id='markerdialog-markercolor-input'>
              <ColorPicker
                value={model.color || ''}
                onChange={e => handleChange(e.toHexString(), 'color')}
                format='hex'
                disabledFormat
              />
            </Form.Item>
          </div>

          {!submitLoading ? (
            <div style={{ display: 'flex', gap: '8px' }}>
              <Form.Item label='&nbsp;'>
                <Tooltip title='Remover Marcador' zIndex={99999} color='#ffbc00'>
                  <Button
                    onClick={() => {
                      if (!model.isNew) {
                        return setShowModalArchiveMarker(true);
                      }

                      removeMarkerOnComplete(model);
                    }}
                    icon={<DeleteOutlined />}
                    id='markerdialog-deletemarker-button'
                  >
                    {isMobile ? '' : 'Excluir'}
                  </Button>
                </Tooltip>
              </Form.Item>

              <Form.Item label='&nbsp;'>
                <Tooltip title='Salvar Alterações' zIndex={99999} color='#ffbc00'>
                  <Button htmlType='submit' icon={<CheckCircleOutlined />} id='markerdialog-submitmarker-button'>
                    {isMobile ? '' : 'Salvar'}
                  </Button>
                </Tooltip>
              </Form.Item>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '30%' }}>
              <Spin />
            </div>
          )}
        </div>
      )}

      {showModalArchiveMarker && (
        <ArchiveMarker
          markerId={model.id}
          markerTitle={model.title}
          onComplete={() => removeMarkerOnComplete(model)}
          handleClose={() => setShowModalArchiveMarker(false)}
          open={showModalArchiveMarker}
        />
      )}
    </Form>
  );
};

export default MarkerEditDialogItem;
